import { p } from "./_helpers";

const cajou = {
 slug: "cajou",
 path: "/secteurs/cajou",

 hero: {
 image: "/photos/nanda-sa-food-hero.jpg",
 alt: p(
 "Noix de cajou de Guinée-Bissau, filière stratégique",
 "Cashew nuts from Guinea-Bissau, strategic value chain",
 "Castanhas de caju da Guiné-Bissau, fileira estratégica"
 ),
 breadcrumb: p("Filière cajou", "Cashew value chain", "Fileira do caju"),
 title: p(
 "Investir dans la filière cajou.",
 "Investing in the cashew value chain.",
 "Investir na fileira do caju."
 ),
 subtitle: p(
 "Une opportunité mondiale, encore peu transformée localement.",
 "A global opportunity, still lightly processed locally.",
 "Uma oportunidade mundial, ainda pouco transformada localmente."
 ),
 },

 seo: {
 title: p(
 "Investir dans la filière cajou en Guinée-Bissau | NANDA SÁ",
 "Investing in the cashew value chain in Guinea-Bissau | NANDA SÁ",
 "Investir na fileira do caju na Guiné-Bissau | NANDA SÁ"
 ),
 description: p(
 "Panorama de la filière cajou en Guinée-Bissau : chiffres clés, chaîne de valeur, segments d'investissement, cadre institutionnel, opportunités pour la diaspora.",
 "Panorama of the cashew value chain in Guinea-Bissau: key figures, value chain, investment segments, institutional framework, opportunities for the diaspora.",
 "Panorama da fileira do caju na Guiné-Bissau: dados-chave, cadeia de valor, segmentos de investimento, enquadramento institucional, oportunidades para a diáspora."
 ),
 keywords: "investir cajou Guinée-Bissau, filière cajou Afrique de l'Ouest, transformation noix de cajou, cashew Guinea-Bissau, Canchungo, Cacheu",
 },

 presentation: {
 title: p(
 "La cajou, pilier économique national.",
 "Cashew, a national economic pillar.",
 "O caju, pilar económico nacional."
 ),
 paragraphs: p(
 [
 "La noix de cajou est un fait de civilisation en Guinée-Bissau. Elle représente entre 80 et 90 % des exportations nationales, place régulièrement le pays parmi les cinq à dix premiers producteurs mondiaux et mobilise plusieurs centaines de milliers de familles rurales.",
 "Pourtant, la majeure partie de la récolte quitte encore le pays à l'état brut. La transformation locale reste minoritaire, alors qu'elle est la principale source de création de valeur. Cette réalité, régulièrement soulignée par la Banque mondiale, la FAO, la BAD et l'AFD, ouvre un espace d'investissement rare à cette échelle en Afrique de l'Ouest.",
 ],
 [
 "The cashew nut is a fact of civilisation in Guinea-Bissau. It accounts for 80 to 90% of national exports, regularly places the country among the top five to ten producers worldwide and mobilises several hundred thousand rural families.",
 "Yet most of the harvest still leaves the country in raw form. Local processing remains a minority activity, whereas it is the main source of value creation. This reality, highlighted by the World Bank, FAO, AfDB and AFD, opens up an investment window rare at this scale in West Africa.",
 ],
 [
 "A castanha de caju é um facto de civilização na Guiné-Bissau. Representa 80 a 90% das exportações nacionais, coloca regularmente o país entre os cinco a dez maiores produtores mundiais e mobiliza várias centenas de milhares de famílias rurais.",
 "Contudo, a maior parte da colheita ainda sai do país em bruto. A transformação local continua minoritária, quando é a principal fonte de criação de valor. Esta realidade, sublinhada pelo Banco Mundial, FAO, BAD e AFD, abre um espaço de investimento raro nesta escala em África Ocidental.",
 ]
 ),
 },

 kpis: {
 title: p("Chiffres clés de la filière", "Key figures of the value chain", "Dados-chave da fileira"),
 items: [
 { n: "85 %", l: p("Des exportations nationales", "Of national exports", "Das exportações nacionais"), d: p("Part moyenne de la cajou dans les exportations.", "Average share of cashew in exports.", "Peso médio do caju nas exportações.") },
 { n: "4-8ᵉ", l: p("Rang mondial", "World rank", "Ranking mundial"), d: p("La Guinée-Bissau parmi les 5 à 10 premiers producteurs.", "Guinea-Bissau among the top 5 to 10 producers.", "A Guiné-Bissau entre os 5 a 10 maiores produtores.") },
 { n: "200 000+", l: p("Tonnes annuelles", "Annual tonnes", "Toneladas anuais"), d: p("Ordre de grandeur de la récolte de noix brutes.", "Order of magnitude of raw cashew harvest.", "Ordem de grandeza da colheita em bruto.") },
 { n: "~10 %", l: p("Transformés localement", "Processed locally", "Transformados localmente"), d: p("Marge de progression considérable.", "Considerable room for growth.", "Margem de progressão considerável.") },
 { n: "Avril–juin", l: p("Cycle de récolte", "Harvest cycle", "Ciclo da colheita"), d: p("Saisonnalité qui structure la filière.", "Seasonality shaping the sector.", "Sazonalidade que estrutura a fileira.") },
 { n: "500 000+", l: p("Foyers concernés", "Households involved", "Famílias envolvidas"), d: p("Familles rurales dépendant du cajou.", "Rural families depending on cashew.", "Famílias rurais dependentes do caju.") },
 ],
 sources: p(
 "Sources : Banque mondiale, ITC Trade Map, FAO, ANCA, estimations BAD et AFD 2023-2025.",
 "Sources: World Bank, ITC Trade Map, FAO, ANCA, AfDB and AFD estimates 2023-2025.",
 "Fontes: Banco Mundial, ITC Trade Map, FAO, ANCA, estimativas BAD e AFD 2023-2025."
 ),
 },

 opportunites: {
 title: p("Où investir dans la filière", "Where to invest", "Onde investir"),
 intro: p(
 "Nous distinguons quatre segments prioritaires pour un investisseur arrivant sur le marché en 2026, chacun avec un ticket d'entrée et un profil de retour distincts.",
 "We identify four priority segments for an investor arriving on the market in 2026, each with a distinct entry ticket and return profile.",
 "Identificamos quatro segmentos prioritários para um investidor que chega ao mercado em 2026, cada um com um ticket de entrada e um perfil de retorno distintos."
 ),
 segments: [
 {
 h3: p("1. Décorticage & transformation moyenne échelle", "1. Medium-scale shelling & processing", "1. Descasque e transformação de média escala"),
 p: p(
 "Le maillon central. Un atelier de 500 à 2 000 t/an capte localement l'essentiel de la marge aujourd'hui exportée. Ticket typique : 300 000 à 1,5 M€. ROI moyen de 4 à 6 ans avec approvisionnement contractualisé auprès des coopératives.",
 "The central link. A 500 to 2,000 t/year plant captures locally most of the currently exported margin. Typical ticket: €300k to €1.5M. Average ROI of 4 to 6 years with contractualised supply from cooperatives.",
 "O elo central. Uma unidade de 500 a 2 000 t/ano capta localmente o essencial da margem hoje exportada. Ticket típico: 300 000 a 1,5 M€. ROI médio de 4 a 6 anos com abastecimento contratualizado."
 ),
 },
 {
 h3: p("2. Produits à valeur ajoutée (beurre, farine, boissons)", "2. Value-added products (butter, flour, beverages)", "2. Produtos de valor acrescentado (manteiga, farinha, bebidas)"),
 p: p(
 "La deuxième transformation permet de doubler ou tripler la valeur unitaire. Marchés cibles : Europe premium, classes moyennes urbaines de la sous-région (Abidjan, Accra). Ticket : 150 000 à 500 000 €.",
 "Second-stage processing can double or triple unit value. Target markets: premium Europe, sub-regional urban middle class (Abidjan, Accra). Ticket: €150k to €500k.",
 "A segunda transformação permite duplicar ou triplicar o valor unitário. Mercados-alvo: Europa premium, classes médias urbanas da sub-região. Ticket: 150 000 a 500 000 €."
 ),
 },
 {
 h3: p("3. Valorisation des coques (CNSL & biomasse)", "3. Shell valorisation (CNSL & biomass)", "3. Valorização das cascas (CNSL & biomassa)"),
 p: p(
 "L'huile de coque (Cashew Nut Shell Liquid) est recherchée en chimie fine, cosmétique et peinture. La biomasse résiduelle peut alimenter chaudières et générateurs. Deux filières encore peu structurées, à fort potentiel.",
 "Cashew Nut Shell Liquid is sought after in fine chemistry, cosmetics and paint. Residual biomass can feed boilers and generators. Two chains still poorly structured, with high potential.",
 "O óleo da casca (Cashew Nut Shell Liquid) é procurado em química fina, cosmética e tintas. A biomassa residual pode alimentar caldeiras e geradores. Duas fileiras ainda pouco estruturadas, de forte potencial."
 ),
 },
 {
 h3: p("4. Logistique & services d'appui", "4. Logistics & support services", "4. Logística e serviços de apoio"),
 p: p(
 "Stockage certifié, transport, contrôle qualité, laboratoire, courtage, ingénierie agroalimentaire : tout un écosystème reste à structurer autour de la transformation. Ticket modéré, forte demande locale.",
 "Certified storage, transport, quality control, laboratory, brokerage, agri-food engineering: a whole ecosystem remains to be structured around processing. Moderate ticket, strong local demand.",
 "Armazenagem certificada, transporte, controlo de qualidade, laboratório, corretagem, engenharia agroalimentar: todo um ecossistema por estruturar em torno da transformação. Ticket moderado, forte procura local."
 ),
 },
 ],
 },

 defis: {
 title: p("Les défis à anticiper", "The challenges to anticipate", "Os desafios a antecipar"),
 intro: p(
 "Une lecture honnête de la filière : les opportunités sont réelles, mais l'entrée dans le marché suppose de prendre en compte plusieurs contraintes structurelles.",
 "An honest reading of the sector: opportunities are real, but entering the market requires taking several structural constraints into account.",
 "Uma leitura honesta da fileira: as oportunidades são reais, mas a entrada no mercado exige a consideração de várias limitações estruturais."
 ),
 items: p(
 [
 "Une saisonnalité concentrée sur trois mois (avril-juin) qui impose une planification logistique rigoureuse.",
 "Un approvisionnement encore atomisé, dépendant de coopératives dont la gouvernance varie.",
 "Un cadre douanier et sanitaire à maîtriser pour l'export international (agréments, certifications HACCP/BRC/IFS).",
 "Un accès au financement bancaire local encore limité pour les investissements industriels moyens.",
 "Une infrastructure énergétique inégale dans les régions productrices, qui peut nécessiter un backup solaire.",
 ],
 [
 "A three-month concentrated seasonality (April-June) requiring rigorous logistics planning.",
 "A still fragmented supply, dependent on cooperatives whose governance varies.",
 "A customs and health framework to master for international export (permits, HACCP/BRC/IFS certifications).",
 "Access to local bank financing still limited for mid-industrial investments.",
 "Uneven energy infrastructure in producing regions, potentially requiring solar backup.",
 ],
 [
 "Uma sazonalidade concentrada em três meses (Abril-Junho) que exige um planeamento logístico rigoroso.",
 "Um abastecimento ainda atomizado, dependente de cooperativas cuja governança varia.",
 "Um enquadramento aduaneiro e sanitário a dominar para a exportação internacional (licenças, certificações HACCP/BRC/IFS).",
 "Um acesso ao financiamento bancário local ainda limitado para investimentos industriais médios.",
 "Uma infra-estrutura energética desigual nas regiões produtoras, que pode requerer backup solar.",
 ]
 ),
 closing: p(
 "Ces défis ne sont pas des obstacles : ce sont des paramètres. Correctement anticipés dès l'étude, ils font la différence entre un projet qui prend l'eau et un projet qui tient.",
 "These challenges are not obstacles: they are parameters. Correctly anticipated from the study phase, they make the difference between a project that sinks and one that holds.",
 "Estes desafios não são obstáculos: são parâmetros. Correctamente antecipados desde o estudo, fazem a diferença entre um projecto que se afunda e um que se mantém."
 ),
 },

 pourquoi: {
 title: p(
 "NANDA SÁ, votre porte d'entrée dans la filière cajou.",
 "NANDA SÁ, your gateway into the cashew value chain.",
 "NANDA SÁ, a sua porta de entrada na fileira do caju."
 ),
 paragraphs: p(
 [
 "NANDA SÁ est ancrée à Canchungo, au cœur de la région productrice de Cacheu, en lien direct avec les coopératives, les autorités et les partenaires internationaux. Nous ne sommes pas un intermédiaire abstrait : nous transformons nous-mêmes la noix de cajou à travers notre maison NANDA SÁ.",
 "Ce positionnement nous permet d'accompagner un investisseur avec une lecture concrète du terrain : approvisionnement, coûts réels, cadre juridique, calendrier, partenaires fiables. Notre pôle Conseil en implantation structure votre projet de A à Z.",
 ],
 [
 "NANDA SÁ is anchored in Canchungo, at the heart of the Cacheu producing region, in direct contact with cooperatives, authorities and international partners. We are not an abstract intermediary: we transform cashew ourselves through our NANDA SÁ house.",
 "This positioning allows us to support investors with a concrete reading of the ground: supply, actual costs, legal framework, timeline, reliable partners. Our Establishment Advisory structures your project end to end.",
 ],
 [
 "A NANDA SÁ está ancorada em Canchungo, no coração da região produtora de Cacheu, em contacto directo com cooperativas, autoridades e parceiros internacionais. Não somos um intermediário abstracto: transformamos nós próprios o caju através da nossa casa NANDA SÁ.",
 "Este posicionamento permite-nos acompanhar um investidor com uma leitura concreta do terreno: abastecimento, custos reais, enquadramento jurídico, calendário, parceiros fiáveis. A nossa Consultoria de implantação estrutura o seu projecto de A a Z.",
 ]
 ),
 bullets: p(
 [
 "Sourcing direct auprès des coopératives de Cacheu, Oio, Bafatá",
 "Étude de marché et positionnement export",
 "Identification du foncier et démarches administratives",
 "Recrutement local et transfert de compétences",
 "Mise en relation avec les partenaires techniques et financiers (BAD, AFD, ANCA)",
 ],
 [
 "Direct sourcing from cooperatives in Cacheu, Oio, Bafatá",
 "Market study and export positioning",
 "Land identification and administrative procedures",
 "Local recruitment and skills transfer",
 "Connection with technical and financial partners (AfDB, AFD, ANCA)",
 ],
 [
 "Sourcing directo junto das cooperativas de Cacheu, Oio, Bafatá",
 "Estudo de mercado e posicionamento na exportação",
 "Identificação do imobiliário e diligências administrativas",
 "Recrutamento local e transferência de competências",
 "Ligação com parceiros técnicos e financeiros (BAD, AFD, ANCA)",
 ]
 ),
 },

 download: {
 title: p(
 "Télécharger la note « Filière cajou · Guinée-Bissau ».",
 "Download the brief « Cashew value chain · Guinea-Bissau ».",
 "Descarregar a nota « Fileira do caju · Guiné-Bissau »."
 ),
 body: p(
 "Une note de synthèse de quelques pages, préparée par NANDA SÁ : chiffres clés, chaîne de valeur, segments d'investissement, défis et contacts utiles.",
 "A concise brief prepared by NANDA SÁ: key figures, value chain, investment segments, challenges and useful contacts.",
 "Uma nota de síntese preparada pela NANDA SÁ: dados-chave, cadeia de valor, segmentos de investimento, desafios e contactos úteis."
 ),
 pdfUrl: "/pdf/nanda-sa-note-cajou.pdf",
 pdfFilename: "nanda-sa-note-cajou.pdf",
 mailSubject: p(
 "Téléchargement de la note « Filière cajou · Guinée-Bissau »",
 "Download of the brief « Cashew value chain · Guinea-Bissau »",
 "Descarga da nota « Fileira do caju · Guiné-Bissau »"
 ),
 mailBody: p(
 "Bonjour, je souhaite recevoir la note « Filière cajou · Guinée-Bissau ». Merci de me tenir informé(e) des prochaines publications de NANDA SÁ.",
 "Hello, I would like to receive the brief « Cashew value chain · Guinea-Bissau ». Please keep me informed of NANDA SÁ's next publications.",
 "Olá, gostaria de receber a nota « Fileira do caju · Guiné-Bissau ». Por favor, mantenha-me informado(a) das próximas publicações da NANDA SÁ."
 ),
 },

 contact: {
 title: p("Parlons de votre projet cajou.", "Let's talk about your cashew project.", "Falemos do seu projecto caju."),
 body: p(
 "Investisseur, transformateur, exportateur, ONG ou diaspora : décrivez votre intention, nous répondons personnellement sous 72 heures.",
 "Investor, processor, exporter, NGO or diaspora: describe your intention, we respond personally within 72 hours.",
 "Investidor, transformador, exportador, ONG ou diáspora: descreva a sua intenção, respondemos pessoalmente em 72 horas."
 ),
 quote: p(
 "Les grandes filières ne s'inventent pas. Elles se construisent, chaîne après chaîne, entre celles et ceux qui les portent.",
 "Great value chains are not invented. They are built, link by link, by those who carry them.",
 "As grandes fileiras não se inventam. Constroem-se, elo a elo, entre aqueles que as sustentam."
 ),
 subject: p("Investir dans la filière cajou", "Invest in the cashew value chain", "Investir na fileira do caju"),
 },

 ctas: ["NandaSa", "Conseil"],
};

export default cajou;
